import { defineComponent } from '../utils/alpine'
import gsap from 'gsap'

export const ButtonHover = defineComponent(() => ({
  isHovering: false,
  mouseX: 0,
  mouseY: 0,
  setSpanX: null as any,
  setSpanY: null as any,
  setArrowX: null as any,
  setArrowY: null as any,
  setSpanOpacity: null as any,
  handleMouseMove: null as ((e: MouseEvent) => void) | null,
  handleMouseEnter: null as ((e: MouseEvent) => void) | null,
  handleMouseLeave: null as ((e: MouseEvent) => void) | null,

  init() {
    const bgSpan = this.$refs.bgSpan as HTMLElement
    const arrow = this.$refs.arrow as HTMLElement
    const iconContainer = this.$refs.iconContainer as HTMLElement

    if (!bgSpan || !arrow || !iconContainer) {
      console.warn('ButtonHover: Missing required refs')
      return
    }

    // Initialize quickSetters for maximum performance
    this.setSpanX = gsap.quickSetter(bgSpan, 'x', 'px')
    this.setSpanY = gsap.quickSetter(bgSpan, 'y', 'px')
    this.setSpanOpacity = gsap.quickSetter(bgSpan, 'opacity')
    this.setArrowX = gsap.quickSetter(arrow, 'x', 'px')
    this.setArrowY = gsap.quickSetter(arrow, 'y', 'px')

    // Hide span initially
    gsap.set(bgSpan, { opacity: 0 })

    // Bind event handlers
    this.handleMouseMove = (e: MouseEvent) => {
      if (!this.isHovering) return

      const rect = iconContainer.getBoundingClientRect()
      const containerWidth = rect.width
      const containerHeight = rect.height

      // Calculate mouse position relative to icon container center (-1 to 1)
      const relativeX = (e.clientX - rect.left) / containerWidth - 0.5
      const relativeY = (e.clientY - rect.top) / containerHeight - 0.5

      // Store normalized mouse position
      this.mouseX = relativeX * 2 // -1 to 1 range
      this.mouseY = relativeY * 2 // -1 to 1 range

      // Arrow moves subtly with mouse (20% of movement)
      const arrowMoveX = this.mouseX * containerWidth * 0.08
      const arrowMoveY = this.mouseY * containerHeight * 0.08

      if (this.setArrowX && this.setArrowY) {
        this.setArrowX(arrowMoveX)
        this.setArrowY(arrowMoveY)
      }
    }

    this.handleMouseEnter = (e: MouseEvent) => {
      this.isHovering = true

      const buttonRect = this.$el.getBoundingClientRect()
      const iconRect = iconContainer.getBoundingClientRect()

      // Calculate mouse position relative to entire button
      const mouseXInButton = e.clientX - buttonRect.left
      const mouseYInButton = e.clientY - buttonRect.top

      // Calculate entry direction based on mouse position
      const fromLeft = mouseXInButton < buttonRect.width * 0.3
      const fromRight = mouseXInButton > buttonRect.width * 0.7
      const fromTop = mouseYInButton < buttonRect.height * 0.3
      const fromBottom = mouseYInButton > buttonRect.height * 0.7

      // Calculate starting position for span relative to icon container
      const iconCenterX = iconRect.left + iconRect.width / 2 - buttonRect.left
      const iconCenterY = iconRect.top + iconRect.height / 2 - buttonRect.top

      let startX = 0
      let startY = 0

      if (fromLeft) {
        startX = -(iconCenterX - mouseXInButton) * 2
      } else if (fromRight) {
        startX = (mouseXInButton - iconCenterX) * 2
      }

      if (fromTop) {
        startY = -(iconCenterY - mouseYInButton) * 2
      } else if (fromBottom) {
        startY = (mouseYInButton - iconCenterY) * 2
      }

      // Set initial position and make visible
      gsap.set(bgSpan, {
        x: startX,
        y: startY,
        opacity: 1,
      })

      // Animate to center position
      gsap.to(bgSpan, {
        x: 0,
        y: 0,
        duration: 0.3,
        ease: 'power2.out',
      })
    }

    this.handleMouseLeave = (e: MouseEvent) => {
      this.isHovering = false

      const buttonRect = this.$el.getBoundingClientRect()
      const iconRect = iconContainer.getBoundingClientRect()

      // Calculate mouse position relative to entire button
      const mouseXInButton = e.clientX - buttonRect.left
      const mouseYInButton = e.clientY - buttonRect.top

      // Calculate exit direction
      const toLeft = mouseXInButton < 0
      const toRight = mouseXInButton > buttonRect.width
      const toTop = mouseYInButton < 0
      const toBottom = mouseYInButton > buttonRect.height

      // Calculate ending position for span
      const iconCenterX = iconRect.left + iconRect.width / 2 - buttonRect.left
      const iconCenterY = iconRect.top + iconRect.height / 2 - buttonRect.top

      let endX = 0
      let endY = 0

      if (toLeft) {
        endX = -(iconCenterX - mouseXInButton) * 2
      } else if (toRight) {
        endX = (mouseXInButton - iconCenterX) * 2
      }

      if (toTop) {
        endY = -(iconCenterY - mouseYInButton) * 2
      } else if (toBottom) {
        endY = (mouseYInButton - iconCenterY) * 2
      }

      // Animate span following mouse out
      gsap.to(bgSpan, {
        x: endX,
        y: endY,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
      })

      // Reset arrow position
      gsap.to(arrow, {
        x: 0,
        y: 0,
        duration: 0.3,
        ease: 'power2.in',
      })
    }

    // Attach event listeners
    this.$el.addEventListener('mousemove', this.handleMouseMove)
    this.$el.addEventListener('mouseenter', this.handleMouseEnter)
    this.$el.addEventListener('mouseleave', this.handleMouseLeave)
  },

  discard() {
    // Remove event listeners
    if (this.handleMouseMove) {
      this.$el.removeEventListener('mousemove', this.handleMouseMove)
    }
    if (this.handleMouseEnter) {
      this.$el.removeEventListener('mouseenter', this.handleMouseEnter)
    }
    if (this.handleMouseLeave) {
      this.$el.removeEventListener('mouseleave', this.handleMouseLeave)
    }

    // Kill any running animations
    const bgSpan = this.$refs.bgSpan as HTMLElement
    const arrow = this.$refs.arrow as HTMLElement

    if (bgSpan) {
      gsap.killTweensOf(bgSpan)
    }
    if (arrow) {
      gsap.killTweensOf(arrow)
    }
  },
}))
